// useHouseholdLists.ts — lädt die konfigurierbaren Haushalts-Listen (Settings)
// und liefert sie an die Dropdowns. Statischer Fallback (HOUSEHOLD_ROLES /
// RELATIONSHIP_OPTIONS) während des Ladens, bei Fehler oder leerer DB → nichts
// bricht. Rollen werden auf die BEKANNTEN keys gefiltert (daran hängen die
// Ableitungsregeln im Code), nur die Labels sind editierbar. Beziehungs-Labels
// sind frei konfigurierbar.

import * as React from "react";
import { listHouseholdRoles, listHouseholdRelationLabels } from "./householdListsService";
import { HOUSEHOLD_ROLES, RELATIONSHIP_OPTIONS, type HouseholdRoleKey } from "./haushalteCleanupService";

export type RoleOption = { key: HouseholdRoleKey; label: string };

// Modul-Cache (kurz), damit nicht jede Komponente neu lädt.
let _rolesCache: RoleOption[] | null = null;
let _labelsCache: string[] | null = null;

/** Cache verwerfen (nach Änderungen in den Einstellungen). */
export function invalidateHouseholdListsCache(): void {
  _rolesCache = null;
  _labelsCache = null;
}

/** Haushaltsrollen (bekannte keys, konfigurierte Labels) mit statischem Fallback. */
export function useHouseholdRoles(): RoleOption[] {
  const [roles, setRoles] = React.useState<RoleOption[]>(_rolesCache ?? HOUSEHOLD_ROLES);
  React.useEffect(() => {
    if (_rolesCache) {
      setRoles(_rolesCache);
      return;
    }
    let alive = true;
    listHouseholdRoles()
      .then((items) => {
        const labelByKey = new Map(items.filter((r) => r.aktiv).map((r) => [r.key, r.label] as const));
        // Nur bekannte Rollen (Reihenfolge/Keys aus dem Code), Label aus der DB.
        // DB leer → static; eine deaktivierte/gelöschte bekannte Rolle fällt raus.
        const mapped: RoleOption[] =
          labelByKey.size === 0
            ? HOUSEHOLD_ROLES
            : HOUSEHOLD_ROLES.filter((r) => labelByKey.has(r.key)).map((r) => ({
                key: r.key,
                label: labelByKey.get(r.key) ?? r.label,
              }));
        const next = mapped.length ? mapped : HOUSEHOLD_ROLES;
        _rolesCache = next;
        if (alive) setRoles(next);
      })
      .catch(() => {
        /* Fehler → statischer Fallback (bleibt) */
      });
    return () => {
      alive = false;
    };
  }, []);
  return roles;
}

/** Manuelle Beziehungs-Labels mit statischem Fallback. */
export function useHouseholdRelationLabels(): string[] {
  const [labels, setLabels] = React.useState<string[]>(_labelsCache ?? RELATIONSHIP_OPTIONS);
  React.useEffect(() => {
    if (_labelsCache) {
      setLabels(_labelsCache);
      return;
    }
    let alive = true;
    listHouseholdRelationLabels()
      .then((items) => {
        const active = items.filter((r) => r.aktiv).map((r) => r.label);
        const next = active.length ? active : RELATIONSHIP_OPTIONS;
        _labelsCache = next;
        if (alive) setLabels(next);
      })
      .catch(() => {
        /* Fehler → statischer Fallback (bleibt) */
      });
    return () => {
      alive = false;
    };
  }, []);
  return labels;
}
